/**
 * /blog (and /zh/blog) — list of all published posts. Pulls metadata
 * from the manifest; doesn't load post bodies (they ship with the
 * bundle anyway, but rendering them here would blow up the index).
 *
 * locale: 'zh' renders zh titles/excerpts where a translation exists,
 * links those posts to their /zh/blog variant, and localizes the chrome.
 */
import { POSTS, localized } from '../content/blog/manifest';
import { MarketingShell } from './MarketingShell';

interface Props {
  /** 'zh' for the Chinese index. Default 'en'. */
  locale?: 'en' | 'zh';
}

export function BlogIndex({ locale = 'en' }: Props) {
  const zh = locale === 'zh';
  return (
    <MarketingShell
      currentLocale={locale}
      hreflangPath="/blog"
      documentTitle={
        zh
          ? '博客 · Draftlize — 与 AI agent 一起的产品思考'
          : 'Blog · Draftlize — Product thinking with AI agents'
      }
      documentDescription={
        zh
          ? '关于 AI 辅助产品工作的文章：卡片图、spec drift、决策日志，以及当 AI agent 既能读也能写你的 substrate 时会发生什么。'
          : 'Essays on AI-assisted product work: card graphs, spec drift, decision logs, and what changes when AI agents can read AND write your substrate.'
      }
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">{zh ? '博客' : 'Blog'}</span>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(44px, 5.6vw, 72px)',
              lineHeight: 1,
              margin: '12px 0 24px',
            }}
          >
            {zh ? (
              <>
                关于<em>与 AI agent 一起做产品思考</em>的文章。
              </>
            ) : (
              <>
                Essays on <em>product thinking with AI agents.</em>
              </>
            )}
          </h1>
          <p className="lede" style={{ maxWidth: 640 }}>
            {zh
              ? '卡片图、spec drift、决策日志、持久化 agent 记忆——以及当你 AI agent 底下的 substrate 不再是聊天历史时，真正改变了什么。'
              : 'Card graphs, spec drift, decision logs, persistent agent memory — and what actually changes when the substrate underneath your AI agents is something other than chat history.'}
          </p>
        </div>
      </section>

      <section className="problem" style={{ paddingTop: 0, paddingBottom: 88 }}>
        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
          }}
        >
          {POSTS.map((p, i) => {
            const L = localized(p, locale);
            // Link to the zh variant only when this post actually has a
            // translation; otherwise the English URL so a zh visitor
            // doesn't land on a /zh/ URL that just shows English.
            const href = zh && p.zh ? `/zh/blog/${p.slug}` : `/blog/${p.slug}`;
            return (
              <a
                key={p.slug}
                href={href}
                style={{
                  display: 'block',
                  padding: '32px 32px',
                  borderBottom:
                    i < POSTS.length - 1 ? '1px solid var(--rule)' : 'none',
                  textDecoration: 'none',
                  color: 'var(--ink-0)',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--paper-1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div
                  className="mono"
                  style={{
                    fontSize: 10.5,
                    letterSpacing: '.12em',
                    color: 'var(--ink-3)',
                    marginBottom: 8,
                  }}
                >
                  {p.date} · {p.readTime}
                </div>
                <h2
                  className="serif"
                  style={{
                    fontSize: 26,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    margin: '0 0 10px',
                    letterSpacing: '-.01em',
                  }}
                >
                  {L.title}
                </h2>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: 'var(--ink-1)',
                    margin: 0,
                    maxWidth: 720,
                  }}
                >
                  {L.excerpt}
                </p>
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: '.1em',
                    color: 'var(--amber)',
                    marginTop: 14,
                    fontWeight: 700,
                  }}
                >
                  {zh ? '阅读 →' : 'READ →'}
                </div>
              </a>
            );
          })}
        </div>
      </section>
    </MarketingShell>
  );
}
